"use client"

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"

export type SliderWithStopsProps = {
  id: string
  label?: string
  values: number[]
  unit?: string
  valueIndex: number
  onIndexChange: (index: number) => void
  formatStopLabel?: (value: number) => string
  showHeader?: boolean
  ariaLabelledBy?: string
  className?: string
  fullWidth?: boolean
}

export function SliderWithStops({
  id,
  label,
  values,
  unit,
  valueIndex,
  onIndexChange,
  formatStopLabel,
  showHeader = true,
  ariaLabelledBy,
  className,
  fullWidth,
}: SliderWithStopsProps) {
  const sliderValue = [valueIndex]
  const formatStop = (stop: number) =>
    formatStopLabel ? formatStopLabel(stop) : unit ? `${stop} ${unit}` : `${stop}`
  const currentStop = values[valueIndex] ?? values[0]

  return (
    <div className={cn("flex w-full flex-col gap-2", fullWidth ? undefined : "max-w-md", className)}>
      {showHeader && label ? (
        <div className="flex items-center justify-between">
          <Label htmlFor={id}>{label}</Label>
          <span className="text-muted-foreground text-sm">{formatStop(currentStop)}</span>
        </div>
      ) : null}
      <Slider
        id={id}
        aria-label={label && !ariaLabelledBy ? label : undefined}
        aria-labelledby={ariaLabelledBy}
        max={Math.max(values.length - 1, 0)}
        min={0}
        onValueChange={(next) => {
          const nextIndex = next[0] ?? 0
          onIndexChange(Math.min(Math.max(nextIndex, 0), values.length - 1))
        }}
        step={1}
        value={sliderValue}
      />
      <div className="relative mt-1 h-4 text-muted-foreground text-xs tabular-nums">
        {values.map((stop, index) => {
          const positionPercent =
            values.length === 1 ? 0 : (index / (values.length - 1)) * 100
          const translateX =
            values.length === 1
              ? "-50%"
              : index === 0
                ? "0%"
                : index === values.length - 1
                  ? "-100%"
                  : "-50%"

          return (
            <span
              key={`${id}-${stop}-${index}`}
              className="absolute block whitespace-nowrap"
              style={{
                left: `${positionPercent}%`,
                transform: `translateX(${translateX})`,
              }}
            >
              {formatStop(stop)}
            </span>
          )
        })}
      </div>
    </div>
  )
}
